import { logError, logValue, measure } from 'lib/observability';
import { createNonPersistentStore } from 'lib/zustand';
import { ErrorWithContext } from 'utils/error';

import { MeasurementContextType, MeasurementIdentifier, MeasurementKey, MeasurementTypes } from './measurementTypes';

export type ProfilingStoreState = {
  lastNavigationStartTimestamp: null | number;
  lastNavigationStartMarkName: null | string;
} & { [Key in MeasurementKey]: MeasurementTypes[Key][] };

const getNavigationStartMark = () => {
  const navigationStart = performance.now();
  const navigationStartMarkName = `client_navigation_${navigationStart}`;
  performance.mark(navigationStartMarkName);
  return { navigationStart, navigationStartMarkName };
};

export const useProfilingStore = createNonPersistentStore<ProfilingStoreState>(
  () => ({
    lastNavigationStartTimestamp: null,
    lastNavigationStartMarkName: getNavigationStartMark().navigationStartMarkName,
    ...(Object.fromEntries(Object.values(MeasurementKey).map(key => [key, []])) as Record<MeasurementKey, []>)
  }),
  {
    isListeningScreenStore: false
  }
);

const identifierToString = (identifier: Record<string, string>) => {
  return Object.entries(identifier)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join(',');
};

const areIdentifiersEqual = (a: Record<string, string>, b: Record<string, string>) => {
  return identifierToString(a) === identifierToString(b);
};

const getPerformanceMarkName = (measurementKey: MeasurementKey, identifier: Record<string, string>) => {
  return `${measurementKey}-${identifierToString(identifier)}`;
};

const getPerformanceMarkStartName = (measurementKey: MeasurementKey, identifier: Record<string, string>) => {
  return `${getPerformanceMarkName(measurementKey, identifier)}-start`;
};

type Params<Key extends MeasurementKey> = {
  measurementKey: Key;
  identifier: MeasurementIdentifier<Key>;
  context: MeasurementContextType<Key>;
};

const startMeasurement = <Key extends MeasurementKey>({
  measurementKey,
  identifier,
  context,
  doNotOverwrite = false
}: Params<Key> & {
  doNotOverwrite?: boolean;
}) => {
  try {
    useProfilingStore.setState(state => {
      const oldMeasurements = state[measurementKey];
      const newMeasurementsWithoutOldInstances = oldMeasurements
        // make sure we remove old instances of the measurement that were not cleaned up previously
        .filter(oldMeasurement => !areIdentifiersEqual(oldMeasurement.measurementId, identifier));
      const isThereConflictingMeasurement = newMeasurementsWithoutOldInstances.length !== oldMeasurements.length;

      if (doNotOverwrite && isThereConflictingMeasurement) {
        return state;
      }

      newMeasurementsWithoutOldInstances.push({
        measurementId: identifier,
        content: {
          ...context,
          startTimestamp: performance.now()
        }
      });

      performance.mark(getPerformanceMarkStartName(measurementKey, identifier));

      return {
        ...state,
        [measurementKey]: newMeasurementsWithoutOldInstances
      };
    });
  } catch (e) {
    logError(e);
  }
};

const endMeasurement = <Key extends MeasurementKey>({ measurementKey, identifier, context }: Params<Key>) => {
  try {
    const state = useProfilingStore.getState();
    const measurement = state[measurementKey].find(measurement => {
      return areIdentifiersEqual(measurement.measurementId, identifier);
    });
    if (!measurement) {
      return;
    }
    const { startTimestamp, ...measurementStartContext } = measurement.content;
    performance.measure(getPerformanceMarkName(measurementKey, identifier), getPerformanceMarkStartName(measurementKey, identifier));
    const endTimestamp = performance.now();
    measure(measurementKey, endTimestamp - startTimestamp, {
      context: {
        ...measurementStartContext,
        ...context,
        ...(identifier as Record<string, string>)
      }
    });
    useProfilingStore.setState(state => ({
      ...state,
      [measurementKey]: state[measurementKey].filter(measurement => !areIdentifiersEqual(measurement.measurementId, identifier))
    }));
  } catch (e) {
    logError(e);
  }
};

/** Clears all measurements for a given key */
const filterMeasurements = <Key extends MeasurementKey>({
  measurementKey,
  filter
}: {
  measurementKey: Key;
  filter: (measurement: MeasurementIdentifier<Key>) => boolean;
}) => {
  useProfilingStore.setState(state => ({
    ...state,
    [measurementKey]: state[measurementKey].filter(measurement => filter(measurement.measurementId))
  }));
};

const getNumberOfMeasurements = <Key extends MeasurementKey>(measurementKey: Key) => {
  return useProfilingStore.getState()[measurementKey].length;
};

const measureAction = async <Result, Key extends MeasurementKey>(cb: () => Promise<Result>, params: Params<Key>): Promise<Result> => {
  try {
    startMeasurement(params);
    const result = await cb();
    endMeasurement(params);
    return result;
  } catch (e) {
    logError(new ErrorWithContext(`${e}`, { measurementKey: params.measurementKey, ...params.context, ...params.identifier }));
    throw e;
  }
};

const measureFromNavigationStart = <Key extends MeasurementKey>({ measurementKey, identifier, context }: Params<Key>) => {
  try {
    const state = useProfilingStore.getState();

    const navigationStart = state.lastNavigationStartTimestamp ?? 0;
    const endTimestamp = performance.now();

    const measurementName = state.lastNavigationStartTimestamp ? `client_navigation_${measurementKey}` : measurementKey;

    performance.measure(getPerformanceMarkName(measurementKey, identifier), useProfilingStore.getState().lastNavigationStartMarkName ?? '');
    measure(measurementName, endTimestamp - navigationStart, {
      context: {
        ...context,
        ...(identifier as Record<string, string>)
      }
    });
  } catch (e) {
    logError(e);
  }
};

const navigate = () => {
  const { navigationStart, navigationStartMarkName } = getNavigationStartMark();
  useProfilingStore.setState({
    lastNavigationStartTimestamp: navigationStart,
    lastNavigationStartMarkName: navigationStartMarkName,
    ...(Object.fromEntries(Object.values(MeasurementKey).map(key => [key, []])) as Record<MeasurementKey, []>)
  });
};

const reportValue = <Key extends MeasurementKey>({
  measurementKey,
  identifier,
  context,
  value
}: {
  measurementKey: Key;
  identifier: MeasurementIdentifier<Key>;
  context?: MeasurementContextType<Key>;
  value: number;
}) => {
  logValue(measurementKey, value, {
    context: {
      ...context,
      ...identifier
    }
  });
};

export const profilingStoreActions = {
  startMeasurement,
  endMeasurement,
  filterMeasurements,
  getNumberOfMeasurements,
  measureAction,
  measureFromNavigationStart,
  navigate,
  reportValue
};
